import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './SetorialDashboard.css';
import EstatisticasHonestas from './EstatisticasHonestas';
import ForaDaAmostra from './ForaDaAmostra';
import EstagioEstrategia from './EstagioEstrategia';
import DadosInsuficientes, { semProntidao } from './DadosInsuficientes';
import { getSetorial } from '../api/brapi';
import { addPosition } from '../api/portfolio';
import PortfolioModal from './PortfolioModal';
import DismissibleNotice from './DismissibleNotice';
import NewsContextCard from './NewsContextCard';
import { formatTradingDate } from '../utils/dates';
import {
  formatBacktestPercent,
  formatProfitFactor,
  formatStrategyHealthBanner,
  formatWeightedProfitFactor,
} from '../utils/backtestRisk';

// ── Info Tooltip ───────────────────────────────────────
function InfoTooltip({ text }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos]         = useState('right');
  const btnRef = useRef(null);

  function handleEnter() {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos(rect.right > window.innerWidth - 240 ? 'left' : 'right');
    }
    setVisible(true);
  }

  return (
    <span
      className="sd-info-wrap"
      onMouseEnter={handleEnter}
      onMouseLeave={() => setVisible(false)}
      onClick={e => e.stopPropagation()}
    >
      <span className="sd-info-icon" ref={btnRef}>!</span>
      {visible && (
        <span className={`sd-info-tooltip sd-info-tooltip--${pos}`}>
          {text}
        </span>
      )}
    </span>
  );
}

function fmtPrice(p) {
  if (p == null) return '—';
  return `R$\u202F${p.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtZ(z) {
  if (z == null) return '—';
  return `${z > 0 ? '+' : ''}${z.toFixed(2)}σ`;
}
function fmtMetricPct(value) {
  const number = Number(value);
  return Number.isFinite(number) ? `${number.toFixed(1).replace('.', ',')}%` : '—';
}
function fmtDays(value) {
  const number = Number(value);
  return Number.isFinite(number) ? `${number.toFixed(1).replace('.', ',')} pregões` : '—';
}
function zClass(z) {
  if (z == null) return 'neutral';
  if (z <= -1.5) return 'neg-strong';
  if (z < 0)    return 'neg-mild';
  if (z > 1.0)  return 'pos';
  return 'neutral';
}
function zToPercent(z) {
  return Math.min(100, Math.max(0, (Math.max(-3, Math.min(3, z)) + 3) / 6 * 100));
}
function diagInfo(diagnosis) {
  switch (diagnosis) {
    case 'systemic':      return { label: 'Sistêmico',      cls: 'sys',  sigLabel: 'Compra forte', sigCls: 'buy' };
    case 'idiosyncratic': return { label: 'Idiossincrático', cls: 'idio', sigLabel: 'Investigar',  sigCls: 'investigate' };
    // FAT-12: Kairi/Z/MM200 ainda não prontos (histórico curto): texto de estado no lugar do diagnóstico e do sinal.
    case 'dados_insuficientes': return { label: 'Dados insuficientes', cls: 'norm', sigLabel: 'Dados insuficientes', sigCls: 'skip' };
    case 'corporate_action_review': return { label: 'Revisar', cls: 'idio', sigLabel: 'Revisar data ex', sigCls: 'investigate' };
    default:              return { label: 'Normal',          cls: 'norm', sigLabel: 'Normal',      sigCls: 'skip' };
  }
}

const TIER_MAP = {
  ITUB4:'S', BBDC4:'S', SANB11:'S', BBAS3:'S',
  VALE3:'S', CSNA3:'S', GGBR4:'S', USIM5:'S',
  PETR4:'A', PETR3:'A', PRIO3:'A', RECV3:'A',
  ELET3:'A', ELET6:'A', ENGI11:'A', EQTL3:'A', CMIG4:'A', CPLE6:'A',
  ABEV3:'A', JBSS3:'A', BRFS3:'A', MDIA3:'A',
  WEGE3:'A', EMBR3:'A', RAIL3:'A', VBBR3:'A',
  LREN3:'B', ASAI3:'B', RADL3:'B', ARZZ3:'B', GRND3:'B',
  HAPV3:'B', RDOR3:'B', FLRY3:'B', DASA3:'B',
  CYRE3:'B', EZTC3:'B', MRVE3:'B', TEND3:'B', RENT3:'B',
  VIVT3:'B', TIMS3:'B',
  MGLU3:'C', AMER3:'C', CASH3:'C', TOTS3:'C', AZUL4:'C', GOLL4:'C',
  BOVA11:'ETF', SMAL11:'ETF', IVVB11:'ETF', FIND11:'ETF', MATB11:'ETF', DIVO11:'ETF',
};
const TIER_META = {
  S:   { label: 'Tier S',  desc: 'Excelentes para mean reversion', cls: 'tier-s',   priority: 0 },
  A:   { label: 'Tier A',  desc: 'Muito bons, operar ativamente',  cls: 'tier-a',   priority: 1 },
  B:   { label: 'Tier B',  desc: 'Funcionam com ressalvas',        cls: 'tier-b',   priority: 2 },
  C:   { label: 'Tier C',  desc: 'Evitar — risco idiossincrático', cls: 'tier-c',   priority: 3 },
  ETF: { label: 'ETFs',    desc: 'Ideal para iniciantes',          cls: 'tier-etf', priority: 4 },
};

function rankAsset(a) {
  const isSystemic = a.diagnosis === 'systemic';
  const tier = TIER_MAP[a.ticker] || 'B';
  const tierPrio = TIER_META[tier]?.priority ?? 2;
  return (isSystemic ? 0 : 1) * 10 + tierPrio;
}

function isActionableSignal(asset) {
  return asset?.belowMa200 !== true
    && asset?.diagnosis === 'systemic';
}

function TradingRestriction({ asset }) {
  if (asset?.needsReview || asset?.signal === 'review') {
    return <span className="sd-mm200-seal">Revisar data ex</span>;
  }
  if (asset?.belowMa200) return <span className="sd-mm200-seal">Não operar (abaixo MM200)</span>;
  return null;
}

function ZGauge({ asset }) {
  if (!asset) return null;
  if (semProntidao(asset)) return <DadosInsuficientes testId="setorial-dados-insuficientes" />;
  const assetPct  = zToPercent(asset.zScore);
  const sectorPct = asset.zSector != null ? zToPercent(asset.zSector) : null;
  const bigGap    = asset.gap != null && asset.gap >= 1.5;
  const TICKS     = [-3, -2, -1, 0, 1, 2, 3];
  return (
    <div className="sd-gauge-wrap">
      <div className="sd-gauge-bar-container">
        <div className="sd-gauge-ticks">
          {TICKS.map(v => (
            <div key={v} className="sd-tick" style={{ left: `${zToPercent(v)}%` }}>
              <span className="sd-tick-label">{v === 0 ? '0' : `${v > 0 ? '+' : ''}${v}σ`}</span>
            </div>
          ))}
        </div>
        <div className="sd-gauge-bar" />
        {sectorPct != null && (
          <div className="sd-gauge-marker sector" style={{ left: `${sectorPct}%` }}>
            <div className="sd-marker-dot sector" />
            <div className="sd-marker-label sector-lbl">Pares: {fmtZ(asset.zSector)}</div>
          </div>
        )}
        <div className="sd-gauge-marker asset" style={{ left: `${assetPct}%` }}>
          <div className="sd-marker-dot asset" />
          <div className="sd-marker-label asset-lbl">{asset.ticker}: {fmtZ(asset.zScore)}</div>
        </div>
      </div>
      <div className="sd-gauge-legend">
        <span className="sd-legend-item"><span className="sd-legend-dot asset" />{asset.ticker}</span>
        {sectorPct != null
          ? <span className="sd-legend-item"><span className="sd-legend-dot sector" />Z-setor sem {asset.ticker}</span>
          : <span className="sd-legend-item">Setor sem pares</span>}
      </div>
      {bigGap && (
        <DismissibleNotice noticeKey={`setorial-gap-${asset.ticker}`} eventKey={`${asset.ticker}:${asset.gap.toFixed(2)}`} closeLabel={`Fechar alerta de gap de ${asset.ticker}`} className="sd-gap-alert" role="status">
          ⚠ Gap de <strong>{asset.gap.toFixed(2)}σ</strong> — evento idiossincrático provável
        </DismissibleNotice>
      )}
    </div>
  );
}

function ZBar({ z }) {
  const pct = Math.min(50, Math.abs(zToPercent(z) - 50));
  const isNeg = z < 0;
  const cls = z <= -2 ? 'neg-strong' : z < -1 ? 'neg-mild' : z > 1 ? 'pos' : 'neutral';
  return (
    <div className="sd-zbar-wrap">
      <div className="sd-zbar-bg">
        <div className="sd-zbar-zero" />
        {isNeg
          ? <div className={`sd-zbar-fill ${cls}`} style={{ width: `${pct}%`, right: '50%' }} />
          : <div className={`sd-zbar-fill ${cls}`} style={{ width: `${pct}%`, left: '50%' }} />}
      </div>
      <div className={`sd-z-value ${zClass(z)}`}>{fmtZ(z)}</div>
    </div>
  );
}

function InsightBox({ asset, sectorName }) {
  if (!asset) return null;
  const { diagnosis } = asset;
  if (diagnosis === 'systemic') {
    return (
      <div className="sd-insight systemic">
        <span className="sd-insight-label">✓ Queda sistêmica</span>
        <strong>{asset.ticker}</strong> cai junto com o setor {sectorName} (gap {asset.gap}σ). Reversão coletiva esperada — setup ideal.
      </div>
    );
  }
  if (diagnosis === 'idiosyncratic') {
    return (
      <>
        <div className="sd-insight danger">
          <span className="sd-insight-label">⚠ Queda idiossincrática</span>
          <strong>{asset.ticker}</strong> cai sozinho (gap {asset.gap}σ vs pares {fmtZ(asset.zSector)}). Investigue causa antes de operar.
        </div>
        <NewsContextCard
          ticker={asset.ticker}
          eventType="queda idiossincrática"
          autoLoad={false}
          compact
        />
      </>
    );
  }
  if (asset.zScore > -1.0) {
    return (
      <div className="sd-insight normal">
        <span className="sd-insight-label">• Zona neutra</span>
        Z de {fmtZ(asset.zScore)} — sem setup. Aguarde distância maior da média.
      </div>
    );
  }
  return (
    <div className="sd-insight watch">
      <span className="sd-insight-label">👁 Watch</span>
      {asset.ticker} se aproxima de entrada (Z: {fmtZ(asset.zScore)}). Aguardar −1,5σ com setor favorável.
    </div>
  );
}

// Sector overview card shown when a sector tab is selected
function SectorOverviewCard({ sector, allAssets, selected, onSelect }) {
  if (!sector) return null;
  const assets = [...(sector.assets || [])].sort((a, b) => (a.zScore ?? 0) - (b.zScore ?? 0));
  const hasSignals = assets.some(isActionableSignal);
  const avgCls = zClass(sector.avgZ);

  return (
    <div className="sd-sector-overview">
      <div className="sd-sov-header">
        <div className="sd-sov-title">
          <span className="sd-sov-emoji">{sector.emoji}</span>
          <span className="sd-sov-name">{sector.name}</span>
          <span className={`sd-sov-z ${avgCls}`}>{fmtZ(sector.avgZ)}</span>
          {!hasSignals && <span className="sd-sov-quiet">Sem sinais ativos</span>}
        </div>
        <div className="sd-sov-count">{assets.length} ativo{assets.length !== 1 ? 's' : ''}</div>
      </div>

      <div className="sd-sov-assets">
        {assets.map(a => {
          const di   = diagInfo(a.diagnosis);
          const tier = TIER_MAP[a.ticker];
          const isSelected = selected === a.ticker;
          return (
            <div
              key={a.ticker}
              className={`sd-sov-row${isSelected ? ' active' : ''}`}
              onClick={() => onSelect(a.ticker)}
            >
              <div className="sd-sov-ticker-col">
                <div className="sd-peer-ticker-row">
                  <span className={`sd-ticker${isSelected ? ' active' : ''}`}>{a.ticker}</span>
                  {tier && <span className={`sd-tier-badge ${TIER_META[tier]?.cls}`}>{tier}</span>}
                </div>
                <div className="sd-ticker-sub">{(a.name || '').substring(0, 22)}</div>
              </div>
              <div className="sd-sov-z-col">
                <ZBar z={a.zScore} />
              </div>
              <div className="sd-sov-price-col">{fmtPrice(a.price)}</div>
              <div className="sd-sov-diag-col">
                <span className={`sd-verdict-cell ${di.cls}`}>{di.label}</span>
                  <TradingRestriction asset={a} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── KPI Modal ────────────────────────────────────────────────────────────────
function KpiModal({ type, data, onClose }) {
  if (!data) return null;

  const { kpis, allAssets, sectors } = data;
  const sectorList = Object.values(sectors)
    .filter(s => s.avgZ != null)
    .sort((a, b) => (a.avgZ || 0) - (b.avgZ || 0));

  const systemic      = allAssets.filter(a => a.diagnosis === 'systemic');
  const idiosyncratic = allAssets.filter(a => a.diagnosis === 'idiosyncratic');
  const filtered      = allAssets.filter(a => a.belowMa200);

  let title, content;

  if (type === 'sectors') {
    title = `${kpis.sectors} Setores Monitorados`;
    content = (
      <table className="sd-modal-table">
        <thead><tr><th>Setor</th><th>Ativos</th><th>Z médio</th><th>Sinais</th></tr></thead>
        <tbody>
          {sectorList.map(s => {
            const signalCount = (s.assets || []).filter(a =>
              a.diagnosis === 'systemic' || a.diagnosis === 'idiosyncratic'
            ).length;
            return (
              <tr key={s.name}>
                <td><span className="sd-modal-sector-name">{s.emoji} {s.name}</span></td>
                <td className="sd-modal-num">{s.assets?.length || 0}</td>
                <td><span className={`sd-z-inline ${zClass(s.avgZ)}`}>{fmtZ(s.avgZ)}</span></td>
                <td>
                  {signalCount > 0
                    ? <span className="sd-modal-signals">{signalCount} ativo{signalCount > 1 ? 's' : ''}</span>
                    : <span className="sd-modal-quiet">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  } else if (type === 'systemic') {
    title = `${kpis.systemic} Ativo${kpis.systemic !== 1 ? 's' : ''} Sistêmico${kpis.systemic !== 1 ? 's' : ''}`;
    content = systemic.length === 0
      ? <p className="sd-modal-empty">Nenhum ativo sistêmico no momento.</p>
      : (
        <>
          <p className="sd-modal-desc">Queda junto com o setor — setup ideal de mean reversion. Z&nbsp;&lt;&nbsp;−1,5 e gap&nbsp;&lt;&nbsp;0,5σ.</p>
          <table className="sd-modal-table">
            <thead><tr><th>Ativo</th><th>Setor</th><th>Z-Ativo</th><th>Z-Setor</th><th>Gap</th><th>Preço</th></tr></thead>
            <tbody>
              {[...systemic].sort((a, b) => a.zScore - b.zScore).map(a => {
                const tier = TIER_MAP[a.ticker];
                return (
                  <tr key={a.ticker}>
                    <td>
                      <div className="sd-peer-ticker-row">
                        <span className="sd-ticker">{a.ticker}</span>
                        {tier && <span className={`sd-tier-badge ${TIER_META[tier]?.cls}`}>{tier}</span>}
                      </div>
                      <div className="sd-ticker-sub">{(a.name || '').substring(0, 20)}</div>
                    </td>
                    <td style={{ fontSize: 11 }}>{a.sectorEmoji} {a.sectorName}</td>
                    <td><span className={`sd-z-inline ${zClass(a.zScore)}`}>{fmtZ(a.zScore)}</span></td>
                    <td>{a.zSector != null ? <span className={`sd-z-inline ${zClass(a.zSector)}`}>{fmtZ(a.zSector)}</span> : <span className="sd-solo">sem pares</span>}</td>
                    <td><span className="sd-gap" style={{ color: '#34d399' }}>{a.gap != null ? `${a.gap.toFixed(2)}σ` : '—'}</span></td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{fmtPrice(a.price)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      );
  } else if (type === 'idiosyncratic') {
    title = `${kpis.idiosyncratic} Ativo${kpis.idiosyncratic !== 1 ? 's' : ''} Idiossincrático${kpis.idiosyncratic !== 1 ? 's' : ''}`;
    content = idiosyncratic.length === 0
      ? <p className="sd-modal-empty">Nenhum ativo idiossincrático no momento.</p>
      : (
        <>
          <p className="sd-modal-desc">Queda isolada do setor — investigar causa antes de operar. Z&nbsp;&lt;&nbsp;−1,5 e gap&nbsp;&gt;&nbsp;1,5σ.</p>
          <table className="sd-modal-table">
            <thead><tr><th>Ativo</th><th>Setor</th><th>Z-Ativo</th><th>Z-Setor</th><th>Gap</th><th>Diagnóstico</th></tr></thead>
            <tbody>
              {[...idiosyncratic].sort((a, b) => a.zScore - b.zScore).map(a => {
                const di  = diagInfo(a.diagnosis);
                const tier = TIER_MAP[a.ticker];
                return (
                  <tr key={a.ticker}>
                    <td>
                      <div className="sd-peer-ticker-row">
                        <span className="sd-ticker">{a.ticker}</span>
                        {tier && <span className={`sd-tier-badge ${TIER_META[tier]?.cls}`}>{tier}</span>}
                      </div>
                      <div className="sd-ticker-sub">{(a.name || '').substring(0, 20)}</div>
                    </td>
                    <td style={{ fontSize: 11 }}>{a.sectorEmoji} {a.sectorName}</td>
                    <td><span className={`sd-z-inline ${zClass(a.zScore)}`}>{fmtZ(a.zScore)}</span></td>
                    <td>{a.zSector != null ? <span className={`sd-z-inline ${zClass(a.zSector)}`}>{fmtZ(a.zSector)}</span> : <span className="sd-solo">sem pares</span>}</td>
                    <td><span className="sd-gap" style={{ color: a.gap >= 2.5 ? '#ef4444' : '#fbbf24' }}>{a.gap != null ? `${a.gap.toFixed(2)}σ` : '—'}</span></td>
                    <td><span className={`sd-verdict-cell ${di.cls}`}>{di.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      );
  } else if (type === 'filtered') {
    title = `${kpis.filtered} Ativo${kpis.filtered !== 1 ? 's' : ''} Filtrado${kpis.filtered !== 1 ? 's' : ''} (MM200)`;
    content = filtered.length === 0
      ? <p className="sd-modal-empty">Nenhum ativo filtrado no momento — todos acima da MM200.</p>
      : (
        <>
          <p className="sd-modal-desc">Preço abaixo da MM(200) — tendência de baixa estrutural. A estratégia Kairi não opera nesses ativos.</p>
          <table className="sd-modal-table">
            <thead><tr><th>Ativo</th><th>Setor</th><th>Preço</th><th>MM200</th><th>Dist.</th><th>Z-Kairi</th></tr></thead>
            <tbody>
              {[...filtered].sort((a, b) => a.zScore - b.zScore).map(a => {
                const tier = TIER_MAP[a.ticker];
                const dist = a.ma200 && a.price ? ((a.price / a.ma200 - 1) * 100).toFixed(1) : null;
                return (
                  <tr key={a.ticker}>
                    <td>
                      <div className="sd-peer-ticker-row">
                        <span className="sd-ticker">{a.ticker}</span>
                        {tier && <span className={`sd-tier-badge ${TIER_META[tier]?.cls}`}>{tier}</span>}
                      </div>
                      <div className="sd-ticker-sub">{(a.name || '').substring(0, 20)}</div>
                    </td>
                    <td style={{ fontSize: 11 }}>{a.sectorEmoji} {a.sectorName}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{fmtPrice(a.price)}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{fmtPrice(a.ma200)}</td>
                    <td><span style={{ color: '#ef4444', fontFamily: 'monospace', fontSize: 12 }}>{dist != null ? `${dist}%` : '—'}</span></td>
                    <td><span className={`sd-z-inline ${zClass(a.zScore)}`}>{fmtZ(a.zScore)}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      );
  } else if (type === 'winrate') {
    const backtest = data.backtest;
    title = 'Desempenho histórico — Setorial';
    content = backtest ? (
      <div className="sd-modal-backtest">
        <div className="sd-backtest-hero">
          <div><span>Expectância ponderada</span><strong>{formatBacktestPercent(backtest.weightedExpectancyPct)}</strong></div>
          <div><span>PF ponderado</span><strong>{formatWeightedProfitFactor(backtest)}</strong></div>
        </div>
        <p className="sd-modal-desc">
          Backtest canônico de dois anos da estratégia Setorial, persistido no PostgreSQL. O Z diário usa somente dados encerrados antes do sinal.
        </p>
        <div className="sd-backtest-grid">
          <div className="wide"><span>Período</span><strong>{formatTradingDate(backtest.periodStart)}–{formatTradingDate(backtest.periodEnd)}</strong></div>
          <div><span>Pregões</span><strong>{backtest.tradingDays ?? '—'}</strong></div>
          <div><span>Trades</span><strong>{backtest.tradesTotal ?? '—'}</strong></div>
          <div><span>Win rate simples</span><strong>{fmtMetricPct(backtest.winRate)}</strong></div>
          <div><span>Win rate ponderado</span><strong>{fmtMetricPct(backtest.weightedWinRate)}</strong></div>
          <div><span>Expectância simples</span><strong>{formatBacktestPercent(backtest.expectancyPct)}</strong></div>
          <div><span>Expectância ponderada</span><strong>{formatBacktestPercent(backtest.weightedExpectancyPct)}</strong></div>
          <div><span>PF simples</span><strong>{formatProfitFactor(backtest)}</strong></div>
          <div><span>PF ponderado</span><strong>{formatWeightedProfitFactor(backtest)}</strong></div>
          <div><span>Drawdown máximo</span><strong>{fmtMetricPct(backtest.maxDrawdownPct)}</strong></div>
          <div><span>Hold médio</span><strong>{fmtDays(backtest.avgHold)}</strong></div>
          <div><span>Pior trade</span><strong>{formatBacktestPercent(backtest.worstTradePct)}</strong></div>
        </div>
        <EstatisticasHonestas backtest={backtest} testId="setorial-estatisticas" />
        <ForaDaAmostra backtest={backtest} testId="setorial-fora-da-amostra" />
        <EstagioEstrategia backtest={backtest} testId="setorial-estagio" />
        <div className="sd-criteria-list" style={{ marginTop: 16 }}>
          <div className="sd-criteria-row"><span className="sd-verdict-cell sys">Entrada</span><span className="sd-criteria-desc">Z-Kairi &lt; −1,5 · diagnóstico sistêmico · acima MM200</span></div>
          <div className="sd-criteria-row"><span className="sd-verdict-cell norm">Saída</span><span className="sd-criteria-desc">Retorno à média (Z ≥ 0), stop em −7% ou 5 pregões</span></div>
          <div className="sd-criteria-row"><span className="sd-verdict-cell idio">Sem look-ahead</span><span className="sd-criteria-desc">Média e desvio do Z usam apenas fechamentos anteriores à data do sinal</span></div>
        </div>
        <p style={{ fontSize: 11, color: '#4a5568', marginTop: 16 }}>
          Resultado passado não garante resultado futuro. Dados: B3 ajustados e persistidos.
        </p>
      </div>
    ) : (
      <div className="sd-backtest-unavailable">
        <strong>Backtest indisponível</strong>
        <p>Execute o job Backtests para calcular e persistir as métricas Setoriais. Novas entradas permanecem bloqueadas até lá.</p>
      </div>
    );
  }

  return (
    <div className="sd-modal-backdrop" onClick={onClose}>
      <div className="sd-modal-box" onClick={e => e.stopPropagation()}>
        <div className="sd-modal-header">
          <span className="sd-modal-title">{title}</span>
          <button className="sd-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="sd-modal-body">{content}</div>
      </div>
    </div>
  );
}

export default function SetorialDashboard({ focusTicker, focusSector, onAssetClick, onSectorFocus }) {
  const [data,         setData]        = useState(null);
  const [loading,      setLoading]     = useState(true);
  const [error,        setError]       = useState(null);
  const [selected,     setSelected]    = useState(null);
  const [sectorView,   setSectorView]  = useState(null); // null = Todos, or sector name
  const [filter,       setFilter]      = useState('Todos');
  const [tierFilter,   setTierFilter]  = useState('all');
  const [showAllPills, setShowAllPills]= useState(false);
  const [lastFetch,    setLastFetch]   = useState(null);
  const [kpiModal,     setKpiModal]    = useState(null);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const resp = await getSetorial();
      setData(resp);
      const best = [...resp.allAssets]
        .filter(isActionableSignal)
        .sort((a, b) => rankAsset(a) - rankAsset(b) || a.zScore - b.zScore)[0]
        || resp.allAssets[0];
      setSelected(best?.ticker || null);
      setLastFetch(Date.now());
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Falha ao carregar dados setoriais');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (focusTicker && data?.allAssets?.some(asset => asset.ticker === focusTicker)) {
      setSelected(focusTicker);
    }
  }, [data, focusTicker]);

  useEffect(() => {
    if (focusSector && data?.sectors?.[focusSector]) setSectorView(focusSector);
  }, [data, focusSector]);

  const selectAsset = useCallback((ticker, options = {}) => {
    const asset = data?.allAssets?.find(item => item.ticker === ticker);
    if (!asset) return;
    setSelected(ticker);
    onAssetClick?.(ticker, asset);
    if (options.sector) {
      setSectorView(asset.sectorName);
      onSectorFocus?.(asset.sectorName);
    }
  }, [data, onAssetClick, onSectorFocus]);

  // Sorted sector list for the tab bar
  const sectorList = useMemo(() => {
    if (!data) return [];
    return Object.values(data.sectors)
      .filter(s => s.avgZ != null)
      .sort((a, b) => (a.avgZ || 0) - (b.avgZ || 0));
  }, [data]);

  const activeSector = sectorView ? data?.sectors?.[sectorView] : null;

  const selectedAsset = useMemo(() => {
    if (!data || !selected) return null;
    return data.allAssets.find(a => a.ticker === selected) || data.allAssets[0];
  }, [data, selected]);

  const selectedSector = selectedAsset ? data?.sectors?.[selectedAsset.sectorName] : null;

  const bestSetup = useMemo(() => {
    if (!data) return null;
    const pool = sectorView
      ? (data.sectors[sectorView]?.assets || [])
      : data.allAssets;
    const candidates = pool.filter(isActionableSignal);
    if (!candidates.length) return null;
    return [...candidates].sort((a, b) => rankAsset(a) - rankAsset(b) || a.zScore - b.zScore)[0];
  }, [data, sectorView]);

  const signalPills = useMemo(() => {
    if (!data) return { signal: [], rest: [] };
    const pool = sectorView
      ? (data.sectors[sectorView]?.assets || [])
      : data.allAssets;
    const signal = pool.filter(isActionableSignal);
    const rest = pool.filter(a => !isActionableSignal(a));
    return { signal, rest };
  }, [data, sectorView]);

  const filteredAssets = useMemo(() => {
    if (!data) return [];
    let arr = sectorView
      ? [...(data.sectors[sectorView]?.assets || [])]
      : [...data.allAssets];
    if (filter === 'Sistêmicos')       arr = arr.filter(a => a.diagnosis === 'systemic');
    if (filter === 'Idiossincráticos') arr = arr.filter(a => a.diagnosis === 'idiosyncratic');
    if (filter === 'Filtrados')        arr = arr.filter(a => a.belowMa200);
    if (tierFilter !== 'all')          arr = arr.filter(a => (TIER_MAP[a.ticker] || 'B') === tierFilter);
    return arr;
  }, [data, sectorView, filter, tierFilter]);

  // When switching sector, auto-select best asset in that sector
  function handleSectorClick(name) {
    if (sectorView === name) {
      setSectorView(null);
      return;
    }
    setSectorView(name);
    setShowAllPills(false);
    if (name && data?.sectors?.[name]) {
      const pool = data.sectors[name].assets || [];
      const best = [...pool]
        .filter(isActionableSignal)
        .sort((a, b) => rankAsset(a) - rankAsset(b) || a.zScore - b.zScore)[0]
        || pool[0];
      if (best) setSelected(best.ticker);
    }
  }

  if (loading) {
    return (
      <div className="sd-root">
        <div className="sd-loading"><div className="sd-spinner" /><span>Carregando análise setorial…</span></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="sd-root">
        <div className="sd-error">⚠ {error} — <button onClick={loadData}>tentar novamente</button></div>
      </div>
    );
  }
  if (!data) return null;

  const { kpis } = data;
  const sel      = selectedAsset;
  const selInfo  = sel ? diagInfo(sel.diagnosis) : null;
  const backtestBlocked = data.riskBacktest?.blocked !== false;
  const portfolioBlocked = Boolean(sel?.belowMa200)
    || sel?.diagnosis === 'idiosyncratic'
    || sel?.needsReview === true
    || sel?.signal === 'review';
  const portfolioBlockReason = sel?.belowMa200
    ? 'Não operar enquanto o ativo estiver abaixo da MM200'
    : sel?.diagnosis === 'idiosyncratic'
      ? 'queda idiossincrática — não operar'
    : sel?.needsReview === true || sel?.signal === 'review'
      ? 'Revise a data ex antes de considerar uma entrada'
    : backtestBlocked
      ? data.riskBacktest?.rule?.text || 'Estratégia bloqueada pela regra canônica'
      : 'Adicionar à carteira';
  const pillsToShow = showAllPills
    ? [...signalPills.signal, ...signalPills.rest]
    : signalPills.signal;

  return (
    <div className="sd-root">
      {data.riskBacktest && (
        <DismissibleNotice noticeKey="setorial-backtest-risk" eventKey={backtestBlocked ? 'blocked' : 'released'} closeLabel="Fechar aviso de saúde da estratégia setorial" className="sd-backtest-warning" role={backtestBlocked ? 'alert' : 'status'} data-testid="setorial-backtest-risk-banner">
          {formatStrategyHealthBanner(data.riskBacktest)}
        </DismissibleNotice>
      )}
      <div className="sd-backtest-warning" role="note">
        Bloqueio reavaliado diariamente com o backtest ponderado (meia-vida 126 pregões).
      </div>

      {/* KPI strip */}
      <div className="sd-kpis">
        <div className="sd-kpi sd-kpi-clickable" onClick={() => setKpiModal('sectors')}>
          <div className="sd-kpi-label">
            Setores
            <InfoTooltip text="Grupos de ativos agrupados por segmento de mercado (Bancos, Energia, Mineração…). Permite comparar o desempenho individual de cada ativo com a média do seu setor." />
          </div>
          <div className="sd-kpi-value">{kpis.sectors}</div>
          <div className="sd-kpi-hint">ver detalhes →</div>
        </div>
        <div className="sd-kpi sd-kpi-clickable" onClick={() => setKpiModal('systemic')}>
          <div className="sd-kpi-label">
            Sistêmicos
            <InfoTooltip text="Ativos cujo Z-Kairi está alinhado com o movimento do setor — a queda é ampla e afeta todo o grupo. Indica oportunidade coletiva: o mercado inteiro recua junto, sem fator específico isolado." />
          </div>
          <div className="sd-kpi-value green">{kpis.systemic}</div>
          <div className="sd-kpi-hint">ver detalhes →</div>
        </div>
        <div className="sd-kpi sd-kpi-clickable" onClick={() => setKpiModal('idiosyncratic')}>
          <div className="sd-kpi-label">
            Idiossincráticos
            <InfoTooltip text="Ativos que caem isoladamente enquanto seu setor permanece neutro ou sobe. Sugere fator específico do papel: resultado ruim, notícia negativa ou evento corporativo. Exige investigação antes de operar." />
          </div>
          <div className="sd-kpi-value orange">{kpis.idiosyncratic}</div>
          <div className="sd-kpi-hint">ver detalhes →</div>
        </div>
        <div className="sd-kpi sd-kpi-clickable" onClick={() => setKpiModal('filtered')}>
          <div className="sd-kpi-label">
            Filtrados MM200
            <InfoTooltip text="Ativos abaixo da Média Móvel de 200 períodos (tendência de baixa). O setup exige preço acima da MM200 como filtro de tendência — estes ativos estão excluídos até recuperarem a tendência." />
          </div>
          <div className="sd-kpi-value muted">{kpis.filtered}</div>
          <div className="sd-kpi-hint">ver detalhes →</div>
        </div>
        <div className="sd-kpi sd-kpi-clickable" onClick={() => setKpiModal('winrate')}>
          <div className="sd-kpi-label">
            Expectância · PF
            <InfoTooltip text="Expectância e profit factor ponderados do backtest Setorial canônico de dois anos. Pesos maiores são atribuídos aos resultados mais recentes." />
          </div>
          <div className="sd-kpi-pair">
            <span><small>EXP</small>{data.backtest ? formatBacktestPercent(data.backtest.weightedExpectancyPct) : '—'}</span>
            <span><small>PF</small>{data.backtest ? formatWeightedProfitFactor(data.backtest) : '—'}</span>
          </div>
          <div className="sd-kpi-hint">ver metodologia →</div>
        </div>
        <div className="sd-kpi sd-kpi-action">
          <button className="sd-refresh-btn" onClick={loadData} disabled={loading}>↻ Atualizar</button>
        </div>
      </div>

      {/* KPI Modals */}
      {kpiModal && <KpiModal type={kpiModal} data={data} onClose={() => setKpiModal(null)} />}

      {/* Sector tab bar */}
      <div className="sd-sector-tabs">
        <button
          className={`sd-sector-tab${!sectorView ? ' active' : ''}`}
          onClick={() => { setSectorView(null); setShowAllPills(false); }}
        >
          Todos
        </button>
        {sectorList.map(s => {
          const hasSignal = s.assets?.some(isActionableSignal);
          const isActive = sectorView === s.name;
          return (
            <button
              key={s.name}
              className={`sd-sector-tab${isActive ? ' active' : ''}`}
              onClick={() => handleSectorClick(s.name)}
              title={`${s.name} · Z médio: ${fmtZ(s.avgZ)}`}
            >
              {s.emoji} {s.name}
              <span className={`sd-stab-z ${zClass(s.avgZ)}`}>{fmtZ(s.avgZ)}</span>
              {hasSignal && <span className="sd-stab-dot" />}
            </button>
          );
        })}
      </div>

      {/* Best setup highlight */}
      {bestSetup ? (
        <div
          className={`sd-best-card ${bestSetup.diagnosis === 'systemic' ? 'best-sys' : 'best-idio'}`}
          onClick={() => selectAsset(bestSetup.ticker)}
          role="button"
        >
          <div className="sd-best-label">
            {bestSetup.diagnosis === 'systemic' ? '★ Melhor Setup' : '⚡ Sinal Ativo'}
            {sectorView ? ` · ${sectorView}` : ' do Dia'}
          </div>
          <div className="sd-best-main">
            <div className="sd-best-ticker">
              {bestSetup.ticker}
              {TIER_MAP[bestSetup.ticker] && (
                <span className={`sd-tier-badge ${TIER_META[TIER_MAP[bestSetup.ticker]]?.cls}`}>
                  {TIER_MAP[bestSetup.ticker]}
                </span>
              )}
            </div>
            <div className="sd-best-meta">
              <span className={`sd-verdict-cell ${diagInfo(bestSetup.diagnosis).cls}`}>
                {diagInfo(bestSetup.diagnosis).label}
              </span>
              <span className={`sd-z-inline ${zClass(bestSetup.zScore)}`}>{fmtZ(bestSetup.zScore)}</span>
              <span className="sd-best-price">{fmtPrice(bestSetup.price)}</span>
              <span className="sd-best-sector">{bestSetup.sectorEmoji} {bestSetup.sectorName}</span>
            </div>
          </div>
          <div className="sd-best-cta">
            {selected === bestSetup.ticker ? 'Selecionado ✓' : 'Ver análise →'}
          </div>
        </div>
      ) : (
        <div className="sd-best-card best-empty">
          <div className="sd-best-label">Setup{sectorView ? ` · ${sectorView}` : ' do Dia'}</div>
          <div className="sd-best-empty-text">
            {sectorView ? `Nenhum sinal ativo em ${sectorView}` : 'Nenhum setup ativo hoje — mercado em zona neutra'}
          </div>
        </div>
      )}

      {/* Body */}
      <div className="sd-body">

        {/* LEFT */}
        <div className="sd-left">

          {/* Sector overview (shown when a sector tab is active) */}
          {sectorView && activeSector && (
            <SectorOverviewCard
              sector={activeSector}
              allAssets={data.allAssets}
              selected={selected}
              onSelect={selectAsset}
            />
          )}

          {/* Individual asset analysis card */}
          {sel && (
            <div className="sd-card sd-sector-card">
              <div className="sd-card-header">
                <div>
                  <div className="sd-card-title">
                    <span className="sd-card-ticker">{sel.ticker}</span>
                    {TIER_MAP[sel.ticker] && (
                      <span className={`sd-tier-badge ${TIER_META[TIER_MAP[sel.ticker]]?.cls}`}>
                        {TIER_MAP[sel.ticker]}
                      </span>
                    )}
                  </div>
                  <div className="sd-card-sub">
                    {selectedSector?.emoji} {sel.sectorName} · {selectedSector?.assets?.length || 1} ativos · {fmtPrice(sel.price)}
                  </div>
                </div>
                {selInfo && (
                  <div className={`sd-verdict ${selInfo.cls}`}>
                    {sel.diagnosis === 'systemic' ? '✓ Sistêmico'
                      : sel.diagnosis === 'idiosyncratic' ? '⚠ Idiossincrático'
                      : sel.zScore <= -1.5 ? '○ Sobrevendido'
                      : '• Normal'}
                  </div>
                )}
                <TradingRestriction asset={sel} />
                <button
                  type="button"
                  className="sd-add-portfolio-btn"
                  disabled={portfolioBlocked}
                  title={portfolioBlockReason}
                  onClick={() => setShowPortfolioModal(true)}
                >
                  {portfolioBlocked ? 'Carteira bloqueada' : '+ Carteira'}
                </button>
              </div>

              {/* InsightBox — immediately below header */}
              <InsightBox asset={sel} sectorName={sel.sectorName} />

              {/* Asset pills — only signals unless expanded */}
              <div className="sd-pills-section">
                <div className="sd-pills">
                  {pillsToShow.map(a => {
                    const tier     = TIER_MAP[a.ticker];
                    const tierMeta = tier ? TIER_META[tier] : null;
                    return (
                      <button
                        key={a.ticker}
                        className={`sd-pill ${selected === a.ticker ? 'active' : ''} sd-pill-${diagInfo(a.diagnosis).cls}`}
                        onClick={() => selectAsset(a.ticker)}
                        title={`${a.ticker} · Z: ${fmtZ(a.zScore)}${tier ? ` · ${tierMeta.label}` : ''}`}
                      >
                        {a.ticker}
                        {tier && <span className={`sd-pill-tier ${tierMeta.cls}`}>{tier}</span>}
                      </button>
                    );
                  })}
                  {signalPills.rest.length > 0 && (
                    <button className="sd-pill-more" onClick={() => setShowAllPills(v => !v)}>
                      {showAllPills ? '↑ Ocultar' : `+${signalPills.rest.length} ativos`}
                    </button>
                  )}
                </div>
                {!showAllPills && signalPills.signal.length === 0 && (
                  <div className="sd-pills-empty">
                    Nenhum sinal ativo — clique em "+{signalPills.rest.length} ativos" para ver todos
                  </div>
                )}
              </div>

              {/* Z-gauge */}
              <div className="sd-gauge-section">
                <div className="sd-gauge-title">Z-Kairi normalizado</div>
                <ZGauge asset={sel} />
              </div>

              {/* Peers table (hidden if showing sector overview since it duplicates) */}
              {!sectorView && selectedSector && selectedSector.assets.length > 1 && (
                <div className="sd-peers">
                  <div className="sd-peers-title">{selectedSector.emoji} Pares — {sel.sectorName}</div>
                  <table className="sd-peers-table">
                    <thead>
                      <tr><th>Ativo</th><th>Preço</th><th>Z-Kairi</th><th>Posição</th></tr>
                    </thead>
                    <tbody>
                      {selectedSector.assets.map(a => {
                        const di = diagInfo(a.diagnosis);
                        const t  = TIER_MAP[a.ticker];
                        return (
                          <tr key={a.ticker}
                            className={a.ticker === sel.ticker ? 'highlighted' : ''}
                            onClick={() => selectAsset(a.ticker)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <div className="sd-peer-ticker-row">
                                <span className={`sd-ticker${a.ticker === sel.ticker ? ' active' : ''}`}>{a.ticker}</span>
                                {t && <span className={`sd-tier-badge ${TIER_META[t]?.cls}`}>{t}</span>}
                              </div>
                              <div className="sd-ticker-sub">{a.name.substring(0, 22)}</div>
                            </td>
                            <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{fmtPrice(a.price)}</td>
                            <td style={{ width: 200 }}><ZBar z={a.zScore} /></td>
                            <td><span className={`sd-verdict-cell ${di.cls}`}>{di.label}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Full scanner */}
          <div className="sd-card">
            <div className="sd-scanner-controls">
              <div className="sd-filters">
                {['Todos', 'Sistêmicos', 'Idiossincráticos', 'Filtrados'].map(f => (
                  <button key={f} className={`sd-filter-btn${filter === f ? ' active' : ''}`} onClick={() => setFilter(f)}>{f}</button>
                ))}
              </div>
              <div className="sd-tier-bar">
                <span className="sd-tier-label">Risco</span>
                <button className={`sd-tier-btn${tierFilter === 'all' ? ' active' : ''}`} onClick={() => setTierFilter('all')}>Todos</button>
                {Object.entries(TIER_META).map(([key, meta]) => (
                  <button key={key} className={`sd-tier-btn ${meta.cls}${tierFilter === key ? ' active' : ''}`}
                    onClick={() => setTierFilter(tierFilter === key ? 'all' : key)} title={meta.desc}>
                    {meta.label}
                  </button>
                ))}
              </div>
            </div>

            <table className="sd-scanner-table">
              <thead>
                <tr>
                  <th>Ativo</th>
                  <th>Setor</th>
                  <th>Z-Ativo</th>
                  <th>Z-Setor</th>
                  <th>Gap</th>
                  <th>Diagnóstico</th>
                  <th>Sinal</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssets.map(a => {
                  const di     = diagInfo(a.diagnosis);
                  const tier   = TIER_MAP[a.ticker];
                  return (
                    <tr key={a.ticker}
                      className={`${selected === a.ticker ? 'selected' : ''}`}
                      onClick={() => selectAsset(a.ticker)}
                    >
                      <td>
                        <div className="sd-peer-ticker-row">
                          <span className="sd-ticker">{a.ticker}</span>
                          {tier && <span className={`sd-tier-badge ${TIER_META[tier]?.cls}`}>{tier}</span>}
                        </div>
                        <div className="sd-ticker-sub">{a.name.substring(0, 20)}</div>
                      </td>
                      <td style={{ fontSize: 11 }}>{a.sectorEmoji} {a.sectorName}</td>
                      <td><span className={`sd-z-inline ${zClass(a.zScore)}`}>{fmtZ(a.zScore)}</span></td>
                      <td>
                        {a.zSector != null
                          ? <span className={`sd-z-inline ${zClass(a.zSector)}`}>{fmtZ(a.zSector)}</span>
                          : <span className="sd-solo">sem pares</span>}
                      </td>
                      <td>
                        {a.gap != null
                          ? <span className="sd-gap" style={{ color: a.gap <= 0.5 ? '#34d399' : a.gap >= 2.5 ? '#ef4444' : a.gap >= 1.5 ? '#fbbf24' : '#9ca3af' }}>
                              {a.gap.toFixed(2)}σ
                            </span>
                          : <span className="sd-solo">—</span>}
                      </td>
                      <td><span className={`sd-verdict-cell ${di.cls}`}>{di.label}</span></td>
                      <td>{a.belowMa200
                        ? <TradingRestriction asset={a} />
                        : <span className={`sd-signal-badge ${di.sigCls}`}>{di.sigLabel}</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {lastFetch && (
              <div className="sd-footer">
                {filteredAssets.length} de {data.allAssets.length} ativos · {data.date ? new Date(data.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—'} · Z-score: média simples de 252 pregões e σ EWMA (meia-vida 63)
              </div>
            )}
          </div>

        </div>

        {/* RIGHT */}
        <div className="sd-right">

          <div className="sd-panel">
            <div className="sd-panel-title">Mapa Setorial</div>
            <div style={{ marginTop: 10 }}>
              {sectorList.map(s => {
                const selectedInSector = selectedAsset?.sectorName === s.name ? selectedAsset : null;
                const mapZ = selectedInSector?.zSector ?? s.avgZ;
                const pct      = Math.min(50, Math.abs(zToPercent(mapZ || 0) - 50));
                const isNeg    = (mapZ || 0) < 0;
                const colorCls = (mapZ || 0) <= -1.5 ? 'green' : (mapZ || 0) >= 1.0 ? 'red' : 'amber';
                const isActive = sectorView === s.name;
                return (
                  <div
                    key={s.name}
                    className={`sd-sector-row${isActive ? ' active' : ''}`}
                     onClick={() => {
                       handleSectorClick(s.name);
                       const candidate = s.assets?.find(isActionableSignal) || s.assets?.[0];
                       if (candidate) selectAsset(candidate.ticker, { sector: true });
                     }}
                    style={{ cursor: 'pointer' }}
                    title={selectedInSector
                      ? `${s.name} · Z-setor sem ${selectedInSector.ticker}: ${fmtZ(mapZ)}`
                      : `${s.name} · Z médio: ${fmtZ(mapZ)}`}
                  >
                    <div className="sd-sector-name">{s.emoji} {s.name}</div>
                    <div className="sd-sector-bar-vis">
                      <div className="sd-sector-zero" />
                      {isNeg
                        ? <div className={`sd-sector-fill ${colorCls}`} style={{ width: `${pct}%`, right: '50%' }} />
                        : <div className={`sd-sector-fill ${colorCls}`} style={{ width: `${pct}%`, left: '50%' }} />}
                    </div>
                    <div className={`sd-sector-z ${zClass(mapZ)}`}>{fmtZ(mapZ)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="sd-panel">
            <div className="sd-panel-title">Qualidade por Tier</div>
            <div className="sd-tier-legend">
              {Object.entries(TIER_META).map(([key, meta]) => (
                <div key={key} className="sd-tier-legend-row">
                  <span className={`sd-tier-badge ${meta.cls}`}>{key}</span>
                  <span className="sd-tier-legend-desc">{meta.desc}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="sd-panel">
            <div className="sd-panel-title">Critérios v3</div>
            <div className="sd-criteria-list">
              <div className="sd-criteria-row"><span className="sd-verdict-cell sys">Sistêmico</span><span className="sd-criteria-desc">Z &lt; −1,5 · gap &lt; 0,5σ</span></div>
              <div className="sd-criteria-row"><span className="sd-verdict-cell idio">Idiossincrático</span><span className="sd-criteria-desc">Z &lt; −1,5 · gap &gt; 1,5σ</span></div>
              <div className="sd-criteria-row"><span className="sd-mm200-seal">Não operar</span><span className="sd-criteria-desc">Abaixo da MM200, sem apagar o diagnóstico</span></div>
            </div>
          </div>

        </div>

      </div>

      {showPortfolioModal && sel && !portfolioBlocked && (
        <PortfolioModal
          ticker={sel.ticker}
          name={sel.name || sel.ticker}
          strategy="Análise Setorial"
          refs={[
            { label: 'Cotação', value: fmtPrice(sel.price) },
            { label: 'Z do ativo', value: fmtZ(sel.zScore), highlight: true },
            { label: 'Z do setor', value: sel.zSector != null ? fmtZ(sel.zSector) : 'Sem pares' },
          ]}
          defaultEntryPrice={sel.price}
          requiresOverride={backtestBlocked}
          strategyHealth={data.riskBacktest}
          onClose={() => setShowPortfolioModal(false)}
          onSave={async (formData) => {
            await addPosition({
              ticker: sel.ticker,
              name: sel.name || sel.ticker,
              ...formData,
            });
            setShowPortfolioModal(false);
          }}
        />
      )}
    </div>
  );
}
